import React, { useMemo, useCallback, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { uniq } from 'lodash-es';
import ModelTag, { ModelTagStyle } from './model-tag';

interface VersionTagsProps {
  /** Version string of the model - used to generate tags */
  version?: string;
  /** Whether this is the latest version - used to generate tags */
  isLatest?: boolean;
  /** Pre-generated tags array - alternative to version/isLatest */
  tags?: string[];
  /** Whether to show the version label (e.g., "Version:") */
  showLabel?: boolean;
  /** Whether to wrap in model-tags container with styling */
  useContainer?: boolean;
  /** Whether tags are interactive/clickable */
  allowInteraction?: boolean;
  /** Callback when a tag is clicked */
  onToggle?: (tag: string) => void;
  /** If true, renders as UI components. If false, returns as children function with string array */
  renderAsComponents?: boolean;
  /** Active tags for visual feedback */
  activeTags?: string[];
}

/**
 * Centralized component for handling version tags display.
 * Can either render as UI components or provide tag strings via children function.
 * Supports both generating tags from version/isLatest props and accepting pre-generated tags.
 */
export const VersionTags = memo(({
  version,
  isLatest,
  tags: providedTags,
  showLabel = false,
  useContainer = false,
  allowInteraction = false,
  onToggle,
  renderAsComponents = true,
  activeTags = [],
  children
}: React.PropsWithChildren<VersionTagsProps>) => {
  const { t } = useTranslation();

  const versionTags = useMemo(() => {
    // If tags are provided directly, use them
    if (providedTags) {
      return uniq(providedTags);
    }

    // Otherwise, generate tags from version and isLatest
    const tags: string[] = [];

    if (isLatest) {
      tags.push(t('customizationPage.modelModalities.latestVersion'));
    }
    
    if (version) {
      tags.push(version);
    }

    return uniq(tags);
  }, [version, isLatest, providedTags, t]);

  const handleClick = useCallback((tag: string) => {
    if (onToggle) {
      onToggle(tag);
    }
  }, [onToggle]);

  // Memoize activeTags as a Set for O(1) lookup performance
  const activeTagsSet = useMemo(() => new Set(activeTags), [activeTags]);

  // If children function is provided, use it regardless of renderAsComponents
  if (children && typeof children === 'function') {
    const childrenFn = children as (tags: string[]) => React.ReactNode;
    return <>{childrenFn(versionTags)}</>;
  }

  // If renderAsComponents is false and no children function, return null
  if (!renderAsComponents) {
    return null;
  }

  // if no tags, return null
  if (versionTags.length === 0) {
    return null;
  }

  const tagElements = versionTags.map(tag => {
    const isTagActive = activeTagsSet.has(tag);
    return (
      <span key={tag} className={useContainer ? "model-tag-container" : undefined}>
        <ModelTag 
          allowInteraction={allowInteraction}
          allowTextSelect={allowInteraction}
          style={allowInteraction ? ModelTagStyle.Highlighted : undefined}
          allTags={[tag]}
          onClick={allowInteraction ? handleClick : undefined}
          isActive={isTagActive}
        >
          {tag}
        </ModelTag>
      </span>
    );
  });

  if (useContainer) {
    return (
      <span className="model-tags">
        {showLabel && (
          <span className="model-tags-label">
            {t('customizationPage.modelTags.version')}:
          </span>
        )}
        {tagElements}
      </span>
    );
  }

  return <>{tagElements}</>;
});

VersionTags.displayName = 'VersionTags';

/**
 * Hook version for cases where you only need the version tag strings
 */
export const useVersionTags = (version?: string, isLatest?: boolean): string[] => {
  const { t } = useTranslation();

  return useMemo(() => {
    const tags: string[] = [];

    
    if (version) {
      tags.push(version);
    }
    if (isLatest) {
      tags.push(t('customizationPage.modelModalities.latestVersion'));
    }

    return tags;
  }, [isLatest, version, t]);
}; 